import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { Trash, Plus, FileText, MagnifyingGlass, CircleNotch, DownloadSimple, Sparkle, Paperclip, X as XIcon } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function KBPage() {
  const confirm = useConfirm();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", kind: "text", tags: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryUpdatedAt, setSummaryUpdatedAt] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/kb");
      setSources(r.data);
    } finally { setLoading(false); }
  };

  const loadSavedSummary = async () => {
    try {
      const r = await api.get("/kb/what-i-know");
      if (r.data?.summary) {
        setSummary(r.data.summary);
        setSummaryUpdatedAt(r.data.updated_at);
      }
    } catch {}
  };

  const generateSummary = async () => {
    setSummarizing(true);
    try {
      const r = await api.post("/kb/what-i-know/generate");
      setSummary(r.data.summary || "");
      setSummaryUpdatedAt(r.data.updated_at);
      toast.success("Summary refreshed.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSummarizing(false); }
  };

  const onFilePick = (f) => {
    if (!f) { setFile(null); return; }
    // Storage tiers:
    //  • Under 30 MB: unlimited uploads.
    //  • 30 - 145 MB: counts against your "large files" quota of 5 (backend enforced).
    //  • Over 145 MB: rejected here, before we send bytes over the wire.
    const HARD_MB  = 145;
    const LARGE_MB = 30;
    const sizeMb = f.size / 1024 / 1024;
    if (sizeMb > HARD_MB) {
      toast.error(`"${f.name}" is ${sizeMb.toFixed(1)} MB. Max ${HARD_MB} MB per file. Split the PDF or paste the text below instead.`);
      return;
    }
    if (sizeMb > LARGE_MB) {
      const largeCount = (sources || []).filter((s) => (s.file_size || 0) > LARGE_MB * 1024 * 1024).length;
      const remaining = Math.max(0, 5 - largeCount);
      if (remaining <= 0) {
        toast.error(`Large-file storage full (5/5 files over ${LARGE_MB} MB). Delete an old large file, or upload files under ${LARGE_MB} MB (those are unlimited).`);
        return;
      }
      toast.info(`This upload uses 1 of your ${remaining} remaining large-file slots (${sizeMb.toFixed(1)} MB).`);
    }
    setFile(f);
    // Auto-fill title (strip extension, humanize), kind, tags — but never overwrite user's edits
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    const humanTitle = f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
    const isImage = ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
    const isDoc = ["pdf", "docx", "doc", "txt", "md"].includes(ext);
    const nextKind = isImage ? "image" : (isDoc ? "doc" : "note");
    const nextTags = ext ? ext : "";
    setForm((prev) => ({
      ...prev,
      title: prev.title?.trim() ? prev.title : humanTitle,
      kind:  prev.kind && prev.kind !== "text" ? prev.kind : nextKind,
      tags:  prev.tags?.trim() ? prev.tags : nextTags,
    }));
  };

  useEffect(() => {
    load();
    loadSavedSummary();
  }, []);

  const [reindexing, setReindexing] = useState(false);
  const reindex = async () => {
    setReindexing(true);
    try {
      const r = await api.post("/kb/reindex");
      toast.success(`Rebuilt semantic index: ${r.data.total_chunks} passages across ${r.data.reindexed_sources} sources.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReindexing(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title || file.name);
        fd.append("tags", form.tags || "");
        await api.post("/kb/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 240000, // 4 minutes for 100+ MB uploads (background summary runs async server-side)
        });
        toast.success(`"${file.name}" added. She's reading it now.`);
      } else {
        await api.post("/kb", {
          title: form.title,
          content: form.content,
          kind: form.kind,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        });
        toast.success("Source added. The assistant just learned something new.");
      }
      setForm({ title: "", content: "", kind: "text", tags: "" });
      setFile(null);
      setShowAdd(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    const src = sources.find(x => x.id === id);
    const ok = await confirm({
      title: "Delete this source?",
      description: `"${src?.title || 'This item'}" will be moved to Trash. You can restore it anytime from there.`,
      confirmText: "Move to Trash",
    });
    if (!ok) return;
    await api.delete(`/kb/${id}`);
    toast.success("Moved to Trash. Restore anytime.");
    load();
  };

  const filtered = sources.filter(s =>
    !q ||
    s.title.toLowerCase().includes(q.toLowerCase()) ||
    (s.content || "").toLowerCase().includes(q.toLowerCase())
  );

  const downloadFile = async (s) => {
    try {
      const r = await api.get(`/kb/${s.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.filename || s.title || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Knowledge base"
        title="Teach her how to create."
        subtitle="Everything you upload becomes part of the assistant's professional education. Playbooks, transcripts, articles, examples. The more you feed her, the more your content sounds like you thinking out loud."
        action={
          <div className="flex items-center gap-2">
            <button
              data-testid="kb-reindex-btn"
              onClick={reindex}
              disabled={reindexing}
              className="btn btn-ghost"
              title="Rebuild the semantic index so she can retrieve passages from your longer documents."
            >
              {reindexing ? <><CircleNotch size={14} className="animate-spin"/> Refreshing…</> : "Refresh"}
            </button>
            <button
              data-testid="kb-add-btn"
              className="btn btn-accent"
              onClick={() => { setFile(null); setForm({ title: "", content: "", kind: "text", tags: "" }); setShowAdd(true); }}
            >
              <Plus size={16}/> Add source
            </button>
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-4 gap-4">
            <div className="eyebrow">{sources.length} sources</div>
            <div className="relative">
              <MagnifyingGlass size={14} style={{position:'absolute', left:12, top:12, color:'var(--ink-3)'}}/>
              <input data-testid="kb-search" className="input pl-9" placeholder="Search sources" value={q} onChange={(e)=>setQ(e.target.value)} />
            </div>
          </div>
          {loading ? <div className="text-sm" style={{color:'var(--ink-3)'}}>Loading…</div> :
            filtered.length === 0 ? (
              <div className="py-12 text-center">
                <div className="eyebrow mb-2">Empty</div>
                <p className="font-serif text-2xl mb-2 font-semibold">She's ready to learn.</p>
                <p className="text-sm" style={{color:'var(--ink-2)'}}>Paste a playbook, a transcript, an article. Anything that makes you a better creator makes her a better manager.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {filtered.map((s) => (
                  <li key={s.id} data-testid={`kb-item-${s.id}`} className="fade-up p-3 flex gap-4 rounded-2xl" style={{ background: "var(--bg-soft)" }}>
                    {s.kind === "image" && s.image_b64 ? (
                      <img alt={s.title} src={`data:image/*;base64,${s.image_b64}`} className="w-16 h-16 rounded-xl object-cover shrink-0"/>
                    ) : (
                      <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center" style={{ background: "var(--accent-tint)", color: "var(--accent-2)" }}>
                        <FileText size={18} weight="duotone"/>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{s.title}</span>
                        <span className="chip">{s.kind}</span>
                        {s.filename && <span className="text-xs font-mono" style={{color:'var(--ink-3)'}}>{s.filename}</span>}
                        {s.tags?.map((t,i)=><span key={i} className="chip">{t}</span>)}
                      </div>
                      {s.summary ? <p className="text-sm mt-1" style={{color:'var(--ink-2)'}}>{s.summary}</p>
                        : <p className="text-sm mt-1" style={{color:'var(--ink-3)'}}>{(s.content||"").slice(0, 200)}…</p>}
                      <div className="text-xs mt-1.5 font-mono" style={{color:'var(--ink-3)'}}>{new Date(s.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-start gap-2">
                      {s.file_size ? (
                        <button onClick={()=>downloadFile(s)} data-testid={`kb-download-${s.id}`}
                           className="p-2 rounded-full hover:bg-white transition-colors" title="Download original">
                          <DownloadSimple size={14}/>
                        </button>
                      ) : null}
                      <button data-testid={`kb-delete-${s.id}`} onClick={()=>remove(s.id)} className="p-2 rounded-full hover:bg-white transition-colors"><Trash size={14}/></button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          }
        </div>

        <aside className="card p-6" data-testid="kb-what-i-know">
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={12} weight="fill" style={{color:'var(--accent)'}}/>
            <div className="eyebrow">What she knows</div>
          </div>
          <h3 className="font-serif text-2xl mb-3 font-semibold">Living summary</h3>
          <button
            data-testid="kb-summarize-btn"
            className="btn btn-ghost w-full mb-4"
            onClick={generateSummary}
            disabled={!sources.length || summarizing}
          >
            {summarizing ? <><CircleNotch size={14} className="animate-spin"/> She's reading…</> : (summary ? "Refresh summary" : "Generate summary")}
          </button>
          {summarizing ? (
            <div className="animate-pulse space-y-2" data-testid="kb-summary-loading">
              <div className="h-3 rounded-full" style={{ background: "var(--paper)", width: "60%" }}/>
              <div className="h-3 rounded-full" style={{ background: "var(--paper)", width: "90%" }}/>
              <div className="h-3 rounded-full" style={{ background: "var(--paper)", width: "40%" }}/>
              <div className="h-3 rounded-full" style={{ background: "var(--paper)", width: "80%" }}/>
              <div className="h-3 rounded-full" style={{ background: "var(--paper)", width: "70%" }}/>
              <p className="text-xs mt-3" style={{color:'var(--ink-3)'}}>Reading your {sources.length} source{sources.length===1?"":"s"}. This usually takes 5-10 seconds.</p>
            </div>
          ) : summary ? (
            <div data-testid="kb-summary-content">
              <Markdown>{summary}</Markdown>
              {summaryUpdatedAt && (
                <p className="text-xs mt-4 font-mono" style={{color:'var(--ink-3)'}}>Last updated {new Date(summaryUpdatedAt).toLocaleString()}</p>
              )}
            </div>
          ) : (
            <p className="text-sm" style={{color:'var(--ink-3)'}}>Click above to audit what the assistant currently believes about your strategy, tactics, and rules. Grouped, human-readable, saved for later.</p>
          )}
        </aside>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{background:'rgba(0,0,0,0.55)'}} data-testid="kb-add-backdrop">
          <form onSubmit={submit} className="card p-6 w-full max-w-2xl fade-up my-8" data-testid="kb-add-form">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="eyebrow mb-1">New source</div>
                <h3 className="font-serif text-3xl font-semibold">Teach her something.</h3>
              </div>
              <button type="button" onClick={()=>{ setShowAdd(false); setFile(null); }} data-testid="kb-add-close" className="p-2 rounded-full hover:bg-[color:var(--paper)]" aria-label="Close"><XIcon size={16}/></button>
            </div>

            {/* File upload area (auto-fills fields when a file is picked) */}
            {file ? (
              <div className="mb-5 rounded-2xl p-4 flex items-center gap-3" data-testid="kb-file-attached" style={{ background: "var(--accent-tint)", border: "1px solid var(--accent)" }}>
                <Paperclip size={18} weight="fill" style={{ color: "var(--accent-2)" }}/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{file.name}</div>
                  <div className="text-[11px] font-mono" style={{ color: "var(--ink-3)" }}>{(file.size / 1024 / 1024).toFixed(2)} MB · text will be extracted after upload</div>
                </div>
                <button type="button" onClick={()=>setFile(null)} data-testid="kb-file-remove" className="p-1.5 rounded-full hover:bg-white" title="Remove attached file"><XIcon size={14}/></button>
              </div>
            ) : (
              <label className="mb-5 block rounded-2xl p-5 text-center cursor-pointer transition" data-testid="kb-file-picker" style={{ background: "var(--bg-soft)", border: "1px dashed var(--line-2)" }} onDragOver={(e)=>{e.preventDefault(); e.currentTarget.style.background='var(--accent-tint)';}} onDragLeave={(e)=>{e.currentTarget.style.background='var(--bg-soft)';}} onDrop={(e)=>{e.preventDefault(); e.currentTarget.style.background='var(--bg-soft)'; const f=e.dataTransfer.files?.[0]; if(f) onFilePick(f);}}>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp,.gif"
                  className="hidden"
                  data-testid="kb-file-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onFilePick(f); e.target.value = ""; }}
                />
                <Paperclip size={20} weight="fill" style={{ color: "var(--accent)" }} className="mx-auto mb-2"/>
                <div className="text-sm font-medium mb-1">Attach a document (optional)</div>
                <div className="text-xs" style={{ color: "var(--ink-3)" }}>
                  PDF, DOCX, TXT, MD, or image. Click or drop. Title, kind and tags fill in automatically.
                </div>
              </label>
            )}

            <label className="block mb-4">
              <div className="eyebrow mb-2">Title</div>
              <input required className="input" data-testid="kb-form-title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Storytelling playbook v3" />
            </label>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <label className="block">
                <div className="eyebrow mb-2">Kind</div>
                <select className="select" data-testid="kb-form-kind" value={form.kind} onChange={(e)=>setForm({...form, kind:e.target.value})}>
                  <option value="text">Text / paste</option>
                  <option value="url">URL</option>
                  <option value="doc">Document</option>
                  <option value="note">Note</option>
                  <option value="image">Image</option>
                </select>
              </label>
              <label className="block">
                <div className="eyebrow mb-2">Tags</div>
                <input className="input" data-testid="kb-form-tags" value={form.tags} onChange={(e)=>setForm({...form, tags:e.target.value})} placeholder="storytelling, hooks" />
              </label>
            </div>
            {!file && (
              <label className="block mb-6">
                <div className="eyebrow mb-2">Content</div>
                <textarea required={!file} className="textarea min-h-[200px]" data-testid="kb-form-content" value={form.content} onChange={(e)=>setForm({...form, content:e.target.value})} placeholder="Paste the source, the URL, or the notes." />
              </label>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-ghost" onClick={()=>{ setShowAdd(false); setFile(null); }}>Cancel</button>
              <button disabled={busy || (!file && !form.content.trim())} type="submit" data-testid="kb-form-submit" className="btn btn-accent">
                {busy ? <><CircleNotch size={13} className="animate-spin"/> Saving…</> : (file ? "Upload & add" : "Add source")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
